<script setup lang="ts">
import type { DurationOption } from '@/types/SearchBar.ts';

interface Props {
  modelValue?: number; // Current selected duration in days
  options?: DurationOption[];
  mode?: 'exact' | 'preset';
  disabled?: boolean;
  maxDuration?: number;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 7,
  options: () => [],
  mode: 'exact',
  disabled: false,
  maxDuration: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [duration: number];
  select: [option: DurationOption];
}>();

/**
 * Check if option is currently selected
 */
function isSelected(option: DurationOption): boolean {
  if (option.type === 'exact') {
    return props.mode === 'exact';
  } else {
    return props.mode === 'preset' && props.modelValue === option.value;
  }
}

/**
 * Check if option should be disabled
 */
function isDisabled(option: DurationOption): boolean {
  if (props.disabled) return true;

  // Exact mode is never disabled
  if (option.type === 'exact') return false;

  if (props.maxDuration !== undefined && option.value > props.maxDuration) {
    return true;
  }

  return false;
}

/**
 * Handle option click
 */
function handleOptionClick(option: DurationOption) {
  if (isDisabled(option)) return;

  emit('select', option);

  if (option.type === 'preset') {
    emit('update:modelValue', option.value);
  }
}

/**
 * Get button classes for each option
 */
function getButtonClasses(option: DurationOption): string[] {
  const classes = [
    'duration-pill',
    'flex',
    'items-center',
    'justify-center',
    'h-10',
    'px-2.5',
    'text-sm',
    'tracking-[0.7px]',
    'font-semibold',
    'transition-colors',
    'whitespace-nowrap',
  ];

  const disabled = isDisabled(option);

  if (disabled) {
    classes.push('opacity-40', 'cursor-not-allowed', 'bg-[#feede9]', 'text-text-secondary');
  } else if (isSelected(option)) {
    classes.push(
      'cursor-pointer',
      'bg-actions-primary',
      'text-white',
      'hover:bg-actions-primary-hover'
    );
  } else {
    classes.push('duration-pill--idle', 'cursor-pointer', 'bg-[#feede9]', 'text-text-secondary');
  }

  return classes;
}
</script>

<template>
  <div
    class="trip-duration-selector flex flex-wrap gap-2"
    role="radiogroup"
    aria-label="Select trip duration"
  >
    <button
      v-for="option in options"
      :key="`${option.type}-${option.value}`"
      type="button"
      role="radio"
      :class="getButtonClasses(option)"
      :aria-checked="isSelected(option)"
      :aria-disabled="isDisabled(option)"
      :disabled="isDisabled(option)"
      @click="handleOptionClick(option)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<style scoped>
.trip-duration-selector {
  font-family: 'Onest', sans-serif;
  line-height: 21px;
}

.duration-pill--idle:hover {
  background-color: #fdd9cf;
}

.duration-pill:focus-visible {
  outline: 2px solid #f54b25; /* actions-primary */
  outline-offset: 2px;
}
</style>
